<template>
  <v-card
    v-if="smAndDown && enableAttendanceV2 && !isDailyAttendanceEnabled()"
    class="mobile-attendance-view"
    flat
  >
    <v-card-text
      v-if="isSchemaLoaded"
      class="pa-0 ox-scroll"
    >
      <div
        aria-labelledby="caption"
        class="fs-16 mb-16"
        role="region"
      >
        <table
          :freeze-second-column-row="shouldShowCheckboxes"
          class="attendance noselect"
          freeze-first-column-row
        >
          <thead>
            <tr>
              <th
                v-if="shouldShowCheckboxes"
                class="b-1 bc-very-light-grey py-2 px-2"
                style="width: 48px"
              >
                <v-checkbox
                  @click="handleSelectAllClick"
                  @update:model-value="toggleAllAttendanceLogs"
                  :indeterminate="isIndeterminate"
                  :model-value="allChecked"
                  data-testId="attendance-log-select-all-mobile"
                  density="compact"
                  hide-details
                  inset
                  tile
                />
              </th>
              <th class="ta-left b-1 bc-very-light-grey py-2 px-2">
                {{ $bct('Student') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="attendanceLog in attendanceLogs"
              :key="attendanceLog.id"
            >
              <td
                v-if="shouldShowCheckboxes"
                :class="logClasses(attendanceLog)"
                style="width: 48px; vertical-align: top"
              >
                <v-checkbox
                  @update:model-value="updateLogSelection(attendanceLog.id, $event)"
                  :data-testId="`attendance-log-select-mobile-${attendanceLog.id}`"
                  :disabled="attendanceLog.submitted"
                  :model-value="isLogSelected(attendanceLog.id)"
                  density="compact"
                  hide-details
                  inset
                  tile
                />
              </td>
              <td :class="logClasses(attendanceLog)">
                <div class="d-flex align-start justify-start">
                  <div class="flex-grow-1 min-w-0">
                    <div>
                      <v-row>
                        <v-col class="ta-left">
                          <a
                            @click="openAttendanceLogEditorBar(attendanceLog)"
                            @click.prevent
                            class="c-primary underlined pointer ta-left"
                            data-testid="attendance-log-name-link"
                            style="cursor: pointer"
                          >
                            {{ attendanceLog.name }}
                          </a>
                        </v-col>
                        <v-col class="ta-center">
                          <v-icon
                            v-if="enableIndividualAttendance && attendanceLog.submitted"
                            :data-testId="`attendance-log-lock-${attendanceLog.id}`"
                            icon="lock"
                            size="x-small"
                            disabled
                          />
                        </v-col>
                        <v-col class="ta-right">
                          <v-btn
                            @click="toggleExpanded(attendanceLog.id)"
                            :aria-label="$bct(`Expand section`)"
                            size="x-small"
                            variant="text"
                            icon
                          >
                            <v-icon>
                              {{ expanded(attendanceLog.id) ? 'expand_less' : 'expand_more' }}
                            </v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                      <p
                        :data-testId="`attendance-log-v2-student-details-${attendanceLog.id}`"
                        class="fs-12 c-grey-70"
                      >
                        ID {{ attendanceLog?.meta?.child?.external_id }} | DOB
                        {{ attendanceLog?.meta?.child?.dob }}
                      </p>
                      <transition-group>
                        <div
                          v-show="expanded(attendanceLog.id)"
                          :key="`details-${attendanceLog.id}`"
                          class="mt-2"
                        >
                          <LongDateTime
                            v-if="attendanceLog?.submitted_at"
                            :date="attendanceLog?.submitted_at"
                            :prefix="`${$bct('Submitted')}`"
                            class="fs-12 c-grey-70"
                          />
                          <v-table
                            class="fs-12 b-1 bc-very-light-grey"
                            density="compact"
                          >
                            <tbody>
                              <tr
                                v-for="question in attendanceLogQuestions"
                                :key="question.id"
                              >
                                <td class="b-1 bc-very-light-grey px-2 py-2">
                                  {{ $bct(question.title) }}
                                </td>
                                <td class="b-1 bc-very-light-grey px-2 py-2">
                                  {{
                                    attendanceLog.custom[
                                      (question.synced_attributes?.[0]?.name?.split('.') || [])[1]
                                    ]
                                  }}
                                </td>
                              </tr>
                            </tbody>
                          </v-table>
                        </div>
                      </transition-group>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <AttendanceLogEditorPagination
          @update:current-page="$emit('update:attendance-log-page', $event)"
          :current-page="currentPage"
          :page-size="pageSize"
          :total-item-count="totalItemsCount"
          :total-pages="totalPages"
        />
      </div>
    </v-card-text>
  </v-card>

  <v-card
    v-else
    flat
  >
    <v-card-text
      v-if="isSchemaLoaded"
      class="pa-0 ox-scroll"
    >
      <div class="mb-2 fs-15">
        {{ $bct(paginationDisplay.label) }}
        <strong v-if="paginationDisplay.end">
          {{ paginationDisplay.start }}-{{ paginationDisplay.end }}
        </strong>
        <strong v-else>
          {{ paginationDisplay.start }}
        </strong>
        {{ $bct(paginationDisplay.suffix) }}
        <strong>{{ paginationDisplay.total }}</strong> {{ $bct('results') }}.
      </div>

      <div
        aria-labelledby="caption"
        class="fs-16 mb-1"
        role="region"
      >
        <table
          :freeze-second-column-row="shouldShowCheckboxes"
          class="attendance noselect"
          freeze-first-column-row
        >
          <thead>
            <tr>
              <th
                v-if="shouldShowCheckboxes"
                class="b-1 bc-very-light-grey py-1 px-2"
              >
                <v-checkbox
                  @click="handleSelectAllClick"
                  @update:model-value="toggleAllAttendanceLogs"
                  :indeterminate="isIndeterminate"
                  :model-value="allChecked"
                  data-testId="attendance-log-select-all"
                  density="compact"
                  hide-details
                  inset
                  tile
                />
              </th>
              <th class="bb-1 ta-left b-1 bc-very-light-grey py-1 px-3">
                {{ $bct('Student') }}
              </th>
              <template v-if="!enableAttendanceV2">
                <th class="bb-1 bt-1 br-1 bc-very-light-grey py-2">
                  {{ $bct('ID') }}
                </th>
                <th class="bb-1 bt-1 br-1 bc-very-light-grey py-2">
                  {{ $bct('DOB') }}
                </th>
              </template>
              <th
                v-if="enableAttendanceV2"
                class="bb-1 bt-1 br-1 bc-very-light-grey py-2 px-3"
              >
                {{ $bct('Month') }}
              </th>
              <th class="bb-1 bt-1 br-1 bc-very-light-grey py-2 px-3">
                {{ $bct('Start date') }}
              </th>
              <th class="bb-1 bt-1 br-1 bc-very-light-grey py-2 px-3">
                {{ $bct('End date') }}
              </th>

              <th
                v-for="question in attendanceLogQuestions"
                :key="question.id"
                :title="$bct(question.title)"
                class="bb-1 bt-1 br-1 bc-very-light-grey py-1 px-3"
              >
                <div
                  class="c-black py-1 rounded d-flex align-center justify-center h-100pc"
                  role="cell"
                >
                  {{ $bct(question.title) }}
                </div>
              </th>

              <template v-if="isDailyAttendanceEnabled()">
                <OperatingLogDay
                  v-for="day in operatingLog.total_days"
                  @click="emit('queue:operating-log-change', day)"
                  :key="day"
                  :day="day"
                  :log="operatingLog"
                  :queue="queuedOperatingLogChanges"
                  :readonly="!!operatingLog.submitted_at || readOnly"
                  :statuses="operatingStatuses"
                />

                <th class="bt-1 br-1 bb-1 bc-very-light-grey ta-center px-2 py-2">
                  {{ operatingLog.total_open }}
                </th>
              </template>

              <template v-if="enableAttendanceV2">
                <th class="bb-1 bt-1 br-1 bc-very-light-grey py-2 px-3">
                  {{ $bct('Submitted') }}
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <v-tooltip
              v-for="attendanceLog in getAttendanceLogs()"
              :key="attendanceLog.id"
              :open-on-click="smAndDown && readOnly"
              :open-on-hover="!smAndDown && readOnly"
              location="bottom"
            >
              <template #activator="{ props: tooltipProps }">
                <tr v-bind="tooltipProps">
                  <td
                    v-if="shouldShowCheckboxes"
                    :class="logClasses(attendanceLog)"
                    style="width: 48px"
                  >
                    <v-checkbox
                      @update:model-value="updateLogSelection(attendanceLog.id, $event)"
                      :data-testId="`attendance-log-select-${attendanceLog.id}`"
                      :disabled="attendanceLog.submitted"
                      :model-value="isLogSelected(attendanceLog.id)"
                      density="compact"
                      hide-details
                      inset
                      tile
                    />
                  </td>
                  <td :class="logClasses(attendanceLog)">
                    <div class="d-flex align-center justify-start">
                      <div class="flex-grow-1">
                        <div class="d-flex">
                          <div
                            v-if="enableAttendanceV2"
                            class="d-flex align-center flex-grow-1"
                          >
                            <a
                              @click="openAttendanceLogEditorBar(attendanceLog)"
                              @click.prevent
                              class="c-primary underlined pointer flex-grow-1"
                              data-testid="attendance-log-name-link"
                              style="cursor: pointer"
                            >
                              {{ attendanceLog.name }}
                            </a>

                            <StandardActionMenu
                              v-if="canRemoveAttendanceLogs"
                              @action-clicked="handleAttendanceLogAction($event, attendanceLog)"
                              :data-testid="`attendance-log-actions-${attendanceLog.id}`"
                              :items="attendanceLogRemovalActions(attendanceLog)"
                              :label="`Actions for ${attendanceLog.name}`"
                              activator-class="ms-1"
                            />
                          </div>

                          <div v-else>
                            <ActionMenu
                              v-if="canRemoveAttendanceLogs"
                              @click:action:delete="emit('delete:attendance-log', attendanceLog)"
                              :button-icon-size="16"
                              :button-title="attendanceLog.name"
                              :items="attendanceLogRemovalActions(attendanceLog)"
                              :label="attendanceLog.name"
                              button-class="px-0"
                              button-icon="more_vert"
                              button-icon-side="right"
                              hover-underline
                            />
                            <span
                              v-else
                              class="c-grey-70"
                              data-testid="attendance-log-name-plain"
                            >
                              {{ attendanceLog.name }}
                            </span>
                          </div>
                          <div class="d-flex">
                            <v-icon
                              v-if="enableIndividualAttendance && attendanceLog.submitted"
                              :data-testId="`attendance-log-lock-${attendanceLog.id}`"
                              icon="lock"
                              size="small"
                              disabled
                            />
                          </div>
                        </div>
                        <div v-if="enableAttendanceV2">
                          <p
                            :data-testId="`attendance-log-v2-student-details-${attendanceLog.id}`"
                            class="fs-12 c-grey-70"
                          >
                            ID {{ attendanceLog?.meta?.child?.external_id }} | DOB
                            {{ attendanceLog?.meta?.child?.dob }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </td>

                  <template v-if="!enableAttendanceV2">
                    <td class="bb-1 br-1 bc-very-light-grey py-1 px-3">
                      {{ attendanceLog?.meta?.child?.external_id }}
                    </td>
                    <td class="bb-1 br-1 bc-very-light-grey py-1 px-3">
                      {{ attendanceLog?.meta?.child?.dob }}
                    </td>
                  </template>
                  <td
                    v-if="enableAttendanceV2"
                    class="bb-1 br-1 bc-very-light-grey py-1 px-3"
                  >
                    {{
                      formatDate(`${attendanceLog?.year}-${attendanceLog?.month}-1`, {
                        shortMonth: true,
                        hideDay: true,
                        hideYear: false,
                      })
                    }}
                  </td>
                  <td class="bb-1 br-1 bc-very-light-grey py-1 px-3">
                    {{ attendanceLog?.meta?.enrollment?.start_date }}
                  </td>
                  <td class="bb-1 br-1 bc-very-light-grey py-1 px-3">
                    {{ attendanceLog?.meta?.enrollment?.end_date }}
                  </td>

                  <AttendanceLogQuestion
                    v-for="question in attendanceLogQuestions"
                    @click="
                      emit('queue:attendance-log-question', {
                        questionId: question.id,
                        attendanceLogId: attendanceLog.id,
                      })
                    "
                    :key="`${question.id}-${attendanceLog.id}`"
                    :attendance-log="attendanceLog"
                    :enable-attendance-v2="enableAttendanceV2"
                    :invalid="isInvalidQuestion(attendanceLog, question)"
                    :operating-log="operatingLog"
                    :question="question"
                    :queued-attendance-log-answers="queuedAttendanceLogAnswers"
                    :queued-attendance-log-question-id="queuedAttendanceLogQuestionId"
                    :readonly="!!operatingLog.submitted_at || enableAttendanceV2"
                    :schema-definition="attendanceLogSchema?.definition"
                    :statuses="attendanceStatuses"
                  />

                  <template v-if="isDailyAttendanceEnabled()">
                    <AttendanceLogDay
                      v-for="day in operatingLog.total_days"
                      @click="emit('queue:attendance-log-change', { log: attendanceLog, day: day })"
                      :key="`${attendanceLog.id}-${day}`"
                      :day="day"
                      :invalid="isInvalidDay(attendanceLog, day)"
                      :log="attendanceLog"
                      :operating-log="operatingLog"
                      :operating-statuses="operatingStatuses"
                      :queue="queuedAttendanceLogChanges"
                      :readonly="!!operatingLog.submitted_at || readOnly"
                      :statuses="attendanceStatuses"
                    />
                    <td class="bb-1 br-1 bc-very-light-grey ta-center px-3">
                      {{ attendanceTotals[attendanceLog.id] }}
                    </td>
                  </template>

                  <template v-if="enableAttendanceV2">
                    <td class="bb-1 br-1 bc-very-light-grey py-1 px-3">
                      <template v-if="attendanceLog.submitted_at">
                        <LongDateTime :date="attendanceLog?.submitted_at" />
                      </template>
                      <template v-else-if="isReadyToSubmit(attendanceLog)">
                        Ready to Submit
                      </template>
                    </td>
                  </template>
                </tr>
              </template>
              <span>{{ $bct(`Filter to a single month to edit ${terms.attendance}.`) }}</span>
            </v-tooltip>
            <template v-if="isDailyAttendanceEnabled()">
              <tr>
                <td
                  v-if="shouldShowCheckboxes"
                  class="bc-very-light-grey bl-1 br-1 bt-1 bb-1"
                ></td>
                <td class="ta-left b-1 bc-very-light-grey px-2">
                  <!-- Child name column -->
                  <!-- v2: also includes external ID and DOB -->
                  {{ $bct('Totals') }}
                </td>
                <td
                  v-if="!enableAttendanceV2"
                  class="bc-very-light-grey br-1 bt-1 bb-1"
                >
                  <!-- v1: external child ID column -->
                </td>

                <td class="bc-very-light-grey br-1 bt-1 bb-1">
                  <!-- v1: date of birth column -->
                  <!-- v2: month column -->
                </td>
                <td class="bc-very-light-grey br-1 bt-1 bb-1">
                  <!-- start_date -->
                </td>
                <td class="bc-very-light-grey br-1 bt-1 bb-1">
                  <!-- v1: start date -->
                  <!-- end_date -->
                </td>
                <td
                  v-for="index in attendanceLogQuestions"
                  :key="['question-total-empty', index].join('-')"
                  class="bc-very-light-grey br-1 bt-1 bb-1"
                >
                  <!-- Empty for attendance log questions cols -->
                </td>
                <td
                  v-for="day in operatingLog.total_days"
                  :key="['total', day].join('-')"
                  class="bc-very-light-grey bt-1 br-1 bb-1"
                >
                  <div
                    class="d-flex align-center justify-center h-100pc"
                    role="cell"
                  >
                    {{ dailyTotals[day] || 0 }}
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <AttendanceLogEditorPagination
          @update:current-page="$emit('update:attendance-log-page', $event)"
          :current-page="currentPage"
          :page-size="pageSize"
          :total-item-count="totalItemsCount"
          :total-pages="totalPages"
        />
      </div>
      <template v-if="submitRevision">
        <p class="fs-14 mt-4">
          <LongDateTime
            :date="submitRevision.created_at"
            :prefix="operatingLog.submitted_at ? 'Submitted on' : 'Previously submitted on'"
            :suffix="`${$bct('by')}  ${submitRevision.author_name}`"
          />
        </p>
      </template>
    </v-card-text>
  </v-card>

  <Teleport
    :disabled="!teleportTargetExists"
    to="#attendance-log-editor-bar"
    defer
  >
    <AttendanceLogEditorBar
      v-if="attendanceLogSchema"
      @change="handleSelectedAttendanceLogChange"
      ref="attendanceLogEditorBar"
      :questions="attendanceLogQuestions"
      :schema="attendanceLogSchema"
    />

    <BulkAttendanceLogEditorBar
      v-if="attendanceLogSchema"
      @change="handleBulkEditChange"
      ref="bulkAttendanceLogEditorBar"
      :attendance-logs="selectedLogs"
      :questions="attendanceLogQuestions"
      :schema="attendanceLogSchema"
    />
  </Teleport>
</template>

<script setup>
import { onMounted } from 'vue';
import { useDisplay } from 'vuetify';
import { useStore } from 'vuex';

import ActionMenu from '@/shared/components/ActionMenu.vue';
import AttendanceLogDay from '@/manager/components/AttendanceLogDay.vue';
import AttendanceLogEditorBar from '@/shared/components/attendance-logs/AttendanceLogEditorBar.vue';
import AttendanceLogEditorPagination from '@/shared/components/attendance-logs/AttendanceLogEditorPagination.vue';
import AttendanceLogQuestion from '@/shared/components/attendance-logs/AttendanceLogQuestion.vue';
import BulkAttendanceLogEditorBar from '@/shared/components/attendance-logs/BulkAttendanceLogEditorBar.vue';
import LongDateTime from '@/shared/components/LongDateTime.vue';
import OperatingLogDay from '@/manager/components/OperatingLogDay.vue';
import useDateFormatting from '@/shared/composables/useDateFormatting';
import useTerms from '@/shared/composables/useTerms';
import { validateAttendance } from '@/shared/services/attendance-validation';

const { formatDate } = useDateFormatting();
const { smAndDown } = useDisplay();
const store = useStore();
const $bct = inject('translator');
const { terms } = useTerms();

const props = defineProps({
  attendanceLogSchema: {
    type: Object,
    default: null,
  },
  attendanceLogs: {
    type: Array,
    default: null,
  },
  attendanceLogQuestions: {
    type: Array,
    default: null,
  },
  attendanceTotals: {
    type: Object,
    default: null,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  dailyTotals: {
    type: Object,
    default: null,
  },
  enableAttendanceV2: {
    type: Boolean,
    default: false,
  },
  enableIndividualAttendance: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: Array,
    default: () => [],
  },
  operatingLog: {
    type: Object,
    default: null,
  },
  operatingLogSchema: {
    type: Object,
    default: null,
  },
  pageSize: {
    type: Number,
    default: 20,
  },
  queuedAttendanceLogChanges: {
    type: Array,
    default: null,
  },
  queuedAttendanceLogQuestionId: {
    type: String,
    default: null,
  },
  queuedAttendanceLogAnswers: {
    type: Array,
    default: null,
  },
  queuedOperatingLogChanges: {
    type: Array,
    default: null,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  selectedAttendanceLogs: {
    type: Array,
    default: () => [],
  },
  submitRevision: {
    type: Object,
    default: null,
  },
  subsidyProgram: {
    type: Object,
    required: true,
  },
  totalItemsCount: {
    type: Number,
    default: 0,
  },
  totalPages: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits([
  'delete:attendance-log',
  'queue:attendance-log-change',
  'queue:attendance-log-question',
  'queue:operating-log-change',
  'update:selected-attendance-logs',
  'change:selected-attendance-log',
  'update:attendance-log-page',
]);

const attendanceStatuses = computed(() => props.subsidyProgram?.attendance_statuses ?? []);
const operatingStatuses = computed(() => props.subsidyProgram?.operating_statuses ?? []);

const attendanceLogEditorBar = ref(null);
const bulkAttendanceLogEditorBar = ref(null);
const expandedAttendanceLogs = ref([]);
const selectedIds = ref(new Set());

const allChecked = computed(() => {
  if (selectableLogs.value.length === 0) return false;
  return selectedIds.value.size === selectableLogs.value.length;
});

const isBulkEditingEnabled = computed(
  () => props.enableAttendanceV2 && props.subsidyProgram?.enable_bulk_editing_attendance,
);

const isFilteredToSingleMonth = computed(() => {
  if (!props.attendanceLogs || props.attendanceLogs.length === 0) return false;

  const firstLog = props.attendanceLogs[0];
  return props.attendanceLogs.every(
    (log) => log.month === firstLog.month && log.year === firstLog.year,
  );
});

const isIndeterminate = computed(() => {
  const selectedCount = selectedIds.value.size;
  return selectedCount > 0 && selectedCount < selectableLogs.value.length;
});

const isSchemaLoaded = computed(() => {
  if (!props.attendanceLogSchema) return false;
  return true;
});

const pageStartCount = computed(() => {
  return props.pageSize * (props.currentPage - 1) + 1;
});

const paginationDisplay = computed(() => {
  const logsCount = props.attendanceLogs?.length ?? 0;
  const isFirstPage = props.currentPage === 1;
  const hasLogs = logsCount > 0;

  if (isFirstPage || !hasLogs) {
    return {
      label: 'Displaying first',
      start: logsCount,
      end: null,
      total: props.totalItemsCount,
      suffix: 'of',
    };
  }

  return {
    label: 'Displaying results',
    start: pageStartCount.value,
    end: pageStartCount.value + logsCount - 1,
    total: props.totalItemsCount,
    suffix: 'out of',
  };
});

const role = computed(() => store.state.role);

const canRemoveAttendanceLogs = computed(() => {
  if (role.value !== 'specialist') return false;

  return !props.subsidyProgram?.enable_linking_attendance_logs_to_invoices;
});

const selectableLogs = computed(() => {
  return props.attendanceLogs?.filter((log) => !log.submitted) ?? [];
});

const selectedLogs = computed(() => {
  if (!props.attendanceLogs) return [];

  return props.attendanceLogs.filter((log) => selectedIds.value.has(log.id));
});

const shouldShowCheckboxes = computed(() => {
  // v1 behavior: show checkboxes when enableIndividualAttendance is true
  if (props.enableIndividualAttendance && !props.enableAttendanceV2) {
    return true;
  }

  // v2 behavior: show checkboxes when bulk editing is enabled and daily attendance constraints are met
  return (
    isBulkEditingEnabled.value && (!isDailyAttendanceEnabled() || isFilteredToSingleMonth.value)
  );
});

const shouldShowSubmitButtonInEditorBar = computed(() => {
  return props.subsidyProgram?.enable_individual_attendance_logs;
});

const teleportTargetExists = ref(false);

onMounted(() => {
  teleportTargetExists.value = document.getElementById('attendance-log-editor-bar') !== null;
});

const getAttendanceLogs = () => {
  return props.attendanceLogs;
};

const attendanceLogRemovalActions = (attendanceLog) => [
  {
    avatar: 'delete',
    event: 'delete',
    testId: `attendance-log-remove-${attendanceLog.id}`,
    title: 'Remove',
  },
];

const handleAttendanceLogAction = ({ action }, attendanceLog) => {
  if (action === 'delete') emit('delete:attendance-log', attendanceLog);
};

const handleSelectedAttendanceLogChange = (event) => {
  emit('change:selected-attendance-log', event);
};

const handleBulkEditChange = () => {
  emit('change:selected-attendance-log');
  clearSelection();
};

function hasAttendanceLogsSelected() {
  return selectedIds.value.size > 0;
}

const openBulkEditor = () => {
  if (selectedIds.value.size > 0 && bulkAttendanceLogEditorBar.value) {
    bulkAttendanceLogEditorBar.value.open();
  }
};

const clearSelection = () => {
  selectedIds.value.clear();
  emit('update:selected-attendance-logs', []);
};

const isLogSelected = (logId) => {
  return selectedIds.value.has(logId);
};

const updateLogSelection = (logId, isSelected) => {
  if (isSelected) {
    selectedIds.value.add(logId);
  } else {
    selectedIds.value.delete(logId);
  }

  const newSelection = Array.from(selectedIds.value);
  emit('update:selected-attendance-logs', newSelection);

  resolveEditorVisibility();
};

// * Handles appropriate opening + closing of both editors when one is already open
// * and the selection changes.
const resolveEditorVisibility = () => {
  if (!attendanceLogEditorBar.value || !bulkAttendanceLogEditorBar.value) return;
  if (!attendanceLogEditorBar.value.isOpen() && !bulkAttendanceLogEditorBar.value.isOpen()) return;

  if (selectedIds.value.size === 0) {
    attendanceLogEditorBar.value.close();
    bulkAttendanceLogEditorBar.value.close();
  } else if (selectedIds.value.size == 1 && bulkAttendanceLogEditorBar.value.isOpen()) {
    attendanceLogEditorBar.value.open(
      selectedLogs.value[0],
      shouldShowSubmitButtonInEditorBar.value,
    );
    bulkAttendanceLogEditorBar.value.close(); // close editor in background
  } else if (selectedIds.value.size > 1 && attendanceLogEditorBar.value.isOpen()) {
    bulkAttendanceLogEditorBar.value.open();
    attendanceLogEditorBar.value.close(); // close editor in background
  }
};

const handleSelectAllClick = (event) => {
  if (isIndeterminate.value) {
    // prevents checkbox component's internal from getting confused when clearing partial selection
    event.preventDefault();
    event.stopPropagation();

    selectedIds.value.clear();
    emit('update:selected-attendance-logs', []);
  }

  resolveEditorVisibility();
};

const toggleAllAttendanceLogs = (isSelected) => {
  if (isSelected) {
    selectableLogs.value.forEach((log) => selectedIds.value.add(log.id));
    emit('update:selected-attendance-logs', Array.from(selectedIds.value));
  } else {
    selectedIds.value.clear();
    emit('update:selected-attendance-logs', []);
  }

  resolveEditorVisibility();
};

const isDailyAttendanceEnabled = () => {
  return props.subsidyProgram?.enable_daily_attendance;
};

const isInvalidDay = (attendanceLog, day) => {
  return props.errors.includes([attendanceLog.id, day].join('-'));
};

const isInvalidQuestion = (attendanceLog, question) => {
  return props.errors.includes([attendanceLog.id, question.id].join('-'));
};

const logClasses = (attendanceLog) => {
  let classes = 'bb-1 ta-left bl-1 br-1 bc-very-light-grey py-1 px-3';
  const containsErrors = props.errors[attendanceLog.id];
  if (containsErrors) {
    classes += ' b-4-red';
  }
  return classes;
};

const openAttendanceLogEditorBar = (attendanceLog) => {
  if (!props.readOnly && attendanceLogEditorBar.value) {
    clearSelection();
    updateLogSelection(attendanceLog.id, true);
    attendanceLogEditorBar.value.open(attendanceLog, shouldShowSubmitButtonInEditorBar.value);
  }
};

const expanded = (attendanceLogId) => {
  return expandedAttendanceLogs.value.includes(attendanceLogId);
};

const toggleExpanded = (attendanceLogId) => {
  const index = expandedAttendanceLogs.value.indexOf(attendanceLogId);
  if (index > -1) {
    expandedAttendanceLogs.value.splice(index, 1);
  } else {
    expandedAttendanceLogs.value.push(attendanceLogId);
  }
};

const isReadyToSubmit = (attendanceLog) => {
  const errors = validateAttendance(
    [attendanceLog],
    props.attendanceLogQuestions,
    props.subsidyProgram.enable_daily_attendance,
  );
  return !errors.length;
};

watch(
  () => props.selectedAttendanceLogs,
  (newSelection) => {
    selectedIds.value = new Set(newSelection);
  },
  { immediate: true },
);

defineExpose({
  clearSelection,
  hasAttendanceLogsSelected,
  openBulkEditor,
});
</script>

<style scoped>
.mobile-attendance-view .v-card-text .attendance {
  width: 100% !important;
  min-width: 100%;
  table-layout: auto;
}
</style>
